import { Button, DatePicker, Form, Input, Select } from "antd";
import styles from "../styles/styles.module.css";
import CustomUpload from "../../../components/CustomUpload/CustomUpload";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectProducts } from "../../../redux/slices/products";
import { fileToBase64 } from "../../../utils/images";
import moment from "moment";
import useVouchers from "../hooks/useVouchers";

const rules = [{ required: true, message: "Este campo es requerido" }];

const VoucherForm = ({ onFinish, voucherSelected }) => {
  const [form] = Form.useForm();
  const [type, setType] = useState(voucherSelected?.type || null);
  const [loading, setLoading] = useState(false);
  const products = useSelector(selectProducts);
  const { onSaveVoucher } = useVouchers();
  const [useVoucherify, setUseVoucherify] = useState(false);

  const submit = async (values) => {
    const image = values?.image
      ? await fileToBase64(values.image.file.originFileObj)
      : null;
    const target = values?.target?.file
      ? await fileToBase64(values.target.file.originFileObj)
      : null;
    const date_begin = moment(values.date_begin).format("YYYY-MM-DD");
    const date_end = moment(values.date_end).format("YYYY-MM-DD");
    const name = values.title;

    const type_value = values.value ?? null;

    const data = {
      ...values,
      image,
      name,
      target,
      date_begin,
      date_end,
      voucherify_id: values?.voucherify_id || null,
      type_value,
    };

    if (voucherSelected) {
      data.coupon_id = voucherSelected.id;
    }

    setLoading(true);
    const res = await onSaveVoucher(data);

    setLoading(false);
    if (res) {
      onFinish();
    }
  };

  const validateDateBegin = (_, value) => {
    const dateEnd = form.getFieldValue("date_end");
    if (!value || !dateEnd || value.isSameOrBefore(dateEnd, "day")) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error(
        "La fecha de inicio no puede ser mayor a la fecha de vencimiento"
      )
    );
  };

  const validateDateEnd = (_, value) => {
    const dateBegin = form.getFieldValue("date_begin");
    if (!value || !dateBegin || value.isSameOrAfter(dateBegin, "day")) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error(
        "La fecha de vencimiento no puede ser menor a la fecha de inicio"
      )
    );
  };

  useEffect(() => {
    if (voucherSelected?.use_voucherify !== undefined) {
      setUseVoucherify(voucherSelected.use_voucherify);
    }
  }, [voucherSelected]);

  return (
    <Form
      form={form}
      onFinish={submit}
      layout="vertical"
      className={styles["voucher-form"]}
      initialValues={{
        date_begin: voucherSelected?.date_start
          ? moment(voucherSelected?.date_start)
          : undefined,
        date_end: voucherSelected?.date_end
          ? moment(voucherSelected?.date_end)
          : undefined,
        title: voucherSelected?.name,
        description: voucherSelected?.description,
        type: voucherSelected?.type,
        value: voucherSelected?.type_value,
        use_voucherify: voucherSelected?.use_voucherify,
        voucherify_id: voucherSelected?.voucherify_id
      }}
    >
      <Form.Item
        rules={[
          { required: !voucherSelected, message: "Este campo es requerido" },
        ]}
        name={"image"}
        label="IMAGEN"
      >
        <CustomUpload
          showRemoveIcon
          className="voucher-upload"
          listType="picture"
          accept="image/*"
        >
          <Button block style={{ width: "100%" }}>
            Seleccionar imagen
          </Button>
        </CustomUpload>
      </Form.Item>

      <Form.Item rules={rules} name={"title"} label="TITULO DEL CUPÓN">
        <Input placeholder="Ej: Descuento 20%" />
      </Form.Item>

      <Form.Item rules={rules} name={"description"} label="DESCRIPCIÓN">
        <Input.TextArea rows={4} />
      </Form.Item>

      <div className={styles["voucher-form-grid"]}>
        <Form.Item
          rules={[...rules, { validator: validateDateBegin }]}
          name={"date_begin"}
          label="FECHA DE INICIO"
        >
          <DatePicker format={"DD-MM-YYYY"} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          rules={[...rules, { validator: validateDateEnd }]}
          name={"date_end"}
          label="FECHA DE VENCIMIENTO"
        >
          <DatePicker format={"DD-MM-YYYY"} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          className={`${
            type !== "DISCOUNT" || !type ? styles["grid-span-full"] : ""
          }`}
          rules={rules}
          name={"type"}
          label="TIPO"
        >
          <Select
            onChange={(value) => {
              setType(value);
              form.setFieldsValue({ value: undefined });
            }}
            options={[
              { label: "Informativo", value: "INFO" },
              { label: "Descuento", value: "DISCOUNT" },
              { label: "Enlace a producto", value: "PRODUCT" },
              { label: "Producto extra", value: "EXTRA" },
            ]}
            placeholder="Seleccionar"
          />
        </Form.Item>

        {type === "DISCOUNT" && (
          <Form.Item
            rules={[
              ...rules,
              {
                type: "number",
                max: 99,
                message: "El descuento no puede ser mayor a 99%",
                transform: (value) => Number(value),
              },
            ]}
            name={"value"}
            label="DESCUENTO"
          >
            <Input />
          </Form.Item>
        )}
      </div>

      {["PRODUCT", "EXTRA"].includes(type) && (
        <Form.Item rules={rules} name={"value"} label="PRODUCTO">
          <Select
            showSearch
            placeholder="Seleccionar"
            optionFilterProp="label"
            options={products?.map((product) => ({
              label: `${product.name} ($${product.price})`,
              value: product.id,
            }))}
          />
        </Form.Item>
      )}

      <Form.Item
        name={"use_voucherify"}
        label="¿Asociar con voucherify?"
        rules={[{ required: true, message: "Selecciona una opción" }]}
      >
        <Select
          placeholder="Seleccionar"
          onChange={(value) => setUseVoucherify(value)}
          options={[
            {
              label: "Si",
              value: true,
            },
            {
              label: "No",
              value: false,
            },
          ]}
        />
      </Form.Item>

      {useVoucherify && (
        <Form.Item
          name={"voucherify_id"}
          label="Código voucherify"
          rules={[{ required: true, message: "Este campo es requerido" }]}
        >
          <Input />
        </Form.Item>
      )}

      <Form.Item name={"target"} label="TARGET SELECTIVO">
        <CustomUpload
          showRemoveIcon
          className="voucher-upload"
          listType="picture"
          accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        >
          <Button block style={{ width: "100%" }}>
            Seleccionar archivo
          </Button>
        </CustomUpload>
      </Form.Item>

      <div style={{ textAlign: "center" }}>
        <Button
          loading={loading}
          htmlType="submit"
          style={{ height: 40, width: 240 }}
          type="primary"
        >
          {voucherSelected ? "EDITAR" : "GUARDAR"} BANNER
        </Button>
      </div>
    </Form>
  );
};

export default VoucherForm;
